import clsx from 'clsx';

import { ButtonSkeleton } from '../Button';
import { ListSkeleton } from '../List';
import { TextSkeleton } from '../Skeleton';

/**
 * Section container labeled.
 * Do not use it around form elements, and instead use `FieldGroup` for it.
 *
 * @example
 * <LabeledContainer>
 *   <LabeledContainerHeader>
 *     <LabeledContainerActionableTitle>
 *       <Label>Label</Label>
 *       <Button label="Action" />
 *     </LabeledContainerActionableTitle>
 *   </LabeledContainerHeader>
 *   <LabeledContainerBody>
 *     <div>Content</div>
 *   </LabeledContainerBody>
 * </LabeledContainer>
 */
export function LabeledContainer(props: { children: React.ReactNode; className?: string }) {
    const { children, className } = props;

    return <div className={clsx('flex flex-col gap-2', className)}>{children}</div>;
}

export function LabeledContainerHeader(props: { children: React.ReactNode; className?: string }) {
    const { children, className } = props;

    return <div className={clsx('flex flex-col gap-0', className)}>{children}</div>;
}

/**
 * Wrap around the label of the section and an action button.
 * @example
 * <LabeledContainerActionableTitle>
 *   <Label>Label</Label>
 *   <Button label="Action" />
 * </LabeledContainerActionableTitle>
 */
export function LabeledContainerActionableTitle(props: {
    children: React.ReactNode;
    className?: string;
}) {
    const { children, className } = props;

    return (
        <div className={clsx('flex flex-row items-center justify-between gap-2', className)}>
            {children}
        </div>
    );
}

export function LabeledContainerBody(props: { children: React.ReactNode; className?: string }) {
    const { children, className } = props;

    return <div className={clsx('flex flex-col gap-2', className)}>{children}</div>;
}

/**
 * Skeleton for a labeled container.
 */
export function LabeledContainerSkeleton(props: { className?: string }) {
    const { className } = props;

    return (
        <LabeledContainer className={className}>
            <LabeledContainerHeader>
                <LabeledContainerActionableTitle>
                    <TextSkeleton>Label</TextSkeleton>
                    <ButtonSkeleton label="Action" iconOnly />
                </LabeledContainerActionableTitle>
            </LabeledContainerHeader>
            <LabeledContainerBody>
                <ListSkeleton items={2} compact />
            </LabeledContainerBody>
        </LabeledContainer>
    );
}
